import { useEffect } from 'react';
import i18n from 'core/i18n/i18n';
import { SUPPORTED_LOCALES } from 'core/utils/numberFormat';
import { mapLanguageToLocale } from 'core/utils/mapLanguageToLocale';

/**
 * Keeps `document.documentElement.lang` in sync with the active i18next language.
 *
 * WCAG 3.1.1 requires the html `lang` attribute to reflect the language of the
 * page's UI. The app's internal language codes ('mx', 'vl') are not valid BCP 47
 * tags, so they are normalized to a supported locale before being written:
 *  - if the code is already a supported BCP 47 locale, it is passed through unchanged.
 *  - otherwise it is mapped via `mapLanguageToLocale` ('mx' → 'es-MX', 'vl' → 'ca', ...).
 *
 * This mirrors the supported-locale normalization used in `core/store/selectors/getLocale`.
 */
export function useHtmlLang(): void {
  useEffect(() => {
    function normalizeLang(lng: string): string {
      if ((SUPPORTED_LOCALES as readonly string[]).includes(lng)) return lng;
      return mapLanguageToLocale(lng);
    }

    function updateHtmlLang(lng: string): void {
      document.documentElement.lang = normalizeLang(lng);
    }

    updateHtmlLang(i18n.language);
    i18n.on('languageChanged', updateHtmlLang);

    return () => {
      i18n.off('languageChanged', updateHtmlLang);
    };
  }, []);
}
